import { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { DevistoLogo } from "@/components/DevistoLogo";
import { getDeviceId, setDeviceId } from "@/lib/device";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-700 bg-slate-950/70 px-4 py-3 text-sm text-slate-100 placeholder:text-[#5C6A87] focus:outline-none focus:ring-2 focus:ring-[#3A5CC8] focus:border-blue-500 transition-colors duration-200";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const navigate = useNavigate();

  const finish = (data) => {
    if (data.device_id) setDeviceId(data.device_id);
    localStorage.setItem("devisto_admin_token", data.token);
    navigate("/admin");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password, device_id: getDeviceId() });
      if (data.mfa_required) { setMfa(data); return; }
      finish(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Connexion impossible. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login/verify`, { challenge: mfa.challenge, code, remember_device: true, device_id: getDeviceId() });
      finish(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Code invalide. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  if (mfa) {
    return (
      <div data-testid="admin-login-page" className="font-body bg-slate-950 text-slate-50 min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="flex justify-center"><DevistoLogo className="h-10 w-auto" variant="white" /></div>
          <h1 className="mt-8 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-center">Vérification en deux étapes</h1>
          <p className="mt-2 text-sm text-[#93A0BC] text-center">Un code à 6 chiffres a été envoyé à {mfa.email_hint}. Il est valable 10 minutes.</p>
          <form onSubmit={verify} data-testid="admin-mfa-form" className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8 space-y-5">
            <input data-testid="admin-mfa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} className={`${inputCls} text-center text-2xl tracking-[0.5em] font-mono`} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required autoFocus />
            {error && <p data-testid="admin-mfa-error" className="text-sm text-red-400">{error}</p>}
            <button type="submit" data-testid="admin-mfa-submit" disabled={loading || code.length !== 6} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#3A5CC8] px-8 py-3.5 text-sm font-semibold text-white disabled:opacity-40">{loading ? "Vérification…" : "Valider"}</button>
            <button type="button" data-testid="admin-mfa-back" onClick={() => { setMfa(null); setCode(""); setError(""); }} className="w-full text-sm text-[#93A0BC] hover:text-white">Retour</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-login-page" className="font-body bg-slate-950 text-slate-50 min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center">
          <Link to="/" data-testid="admin-login-logo"><DevistoLogo className="h-10 w-auto" variant="white" /></Link>
        </div>
        <h1 className="mt-8 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-center">Espace admin</h1>
        <p className="mt-2 text-sm text-[#93A0BC] text-center">Accédez aux demandes et aux partenaires.</p>
        <form onSubmit={submit} className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8 space-y-5">
          <div>
            <label htmlFor="admin-email" className="block text-sm font-medium text-slate-300 mb-2">Email</label>
            <input id="admin-email" type="email" data-testid="admin-email" className={inputCls} placeholder="vous@exemple.be" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-sm font-medium text-slate-300 mb-2">Mot de passe</label>
            <input id="admin-password" type="password" data-testid="admin-password" className={inputCls} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {error && <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#3A5CC8] px-8 py-3.5 text-sm font-semibold text-white shadow-[0_6px_16px_-8px_rgba(58,92,200,0.5)] hover:bg-[#3A5CC8] disabled:opacity-40 transition-[background-color] duration-200 focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]"
          >
            {loading ? "Connexion…" : "Se connecter"} <ArrowRight className="w-4 h-4" />
          </button>
          <Link to="/mot-de-passe-oublie" data-testid="admin-forgot-password" className="block text-center text-xs font-semibold text-[#9FB4F2] hover:text-white">
            Mot de passe oublié ? Recevoir un lien par e-mail
          </Link>
        </form>
      </div>
    </div>
  );
}
